import type { FormRegularStep, FormStep, StepItem } from "../form-types";

type RichTextStepItem = Extract<StepItem, { _type: "richTextBlock" }>;
type ReviewStarsStepItem = Extract<StepItem, { _type: "reviewStars" }>;
type RadioStepItem = Extract<StepItem, { _type: "radioInput" }>;

type ButtonStackStepMapping =
  | { kind: "fallback" }
  | {
      flowBefore: StepItem[];
      headline?: RichTextStepItem;
      kind: "buttonStack";
      radioInput: RadioStepItem;
      reviewStars?: ReviewStarsStepItem;
      subhead?: RichTextStepItem;
      trailingItems: StepItem[];
    };

/**
 * `friendly` is the lime brand button. The button-stack hero always uses it
 * for the phone link, whatever color the step stored for the default layout.
 */
function withLimePhoneLink(item: StepItem): StepItem {
  if (item._type !== "phoneLinkCta") {
    return item;
  }

  return { ...item, colorVariant: "friendly" };
}

/**
 * Portable text → a single label string for the radio divider. Spans in a
 * block join without spaces so "Who" + " needs care?" stays one phrase.
 */
function plainTextFromRichTextBlock(
  block: RichTextStepItem,
): string | undefined {
  if (!Array.isArray(block.text)) {
    return undefined;
  }

  const parts: string[] = [];

  for (const item of block.text) {
    if (item._type !== "block" || !Array.isArray(item.children)) {
      continue;
    }

    let blockText = "";
    for (const child of item.children) {
      if (child._type === "span" && typeof child.text === "string") {
        blockText += child.text;
      }
    }

    if (blockText.trim()) {
      parts.push(blockText.trim());
    }
  }

  const plainText = parts.join(" ").trim();
  return plainText || undefined;
}

/**
 * Production step 1 stores the question as a rich text block with a null
 * radio label. Figma draws that question as the lined divider, so the last
 * rich text before the radio becomes the legend when the radio has none.
 */
function withDividerFromPrecedingRichText(
  radioInput: RadioStepItem,
  middleItems: StepItem[],
): { middleItems: StepItem[]; radioInput: RadioStepItem } {
  if (radioInput.buttonLabel?.trim()) {
    return { middleItems, radioInput };
  }

  for (let index = middleItems.length - 1; index >= 0; index -= 1) {
    const item = middleItems[index];
    if (item?._type !== "richTextBlock") {
      continue;
    }

    const label = plainTextFromRichTextBlock(item);
    if (!label) {
      continue;
    }

    return {
      middleItems: [
        ...middleItems.slice(0, index),
        ...middleItems.slice(index + 1),
      ],
      radioInput: { ...radioInput, buttonLabel: label },
    };
  }

  return { middleItems, radioInput };
}

/**
 * Splits step 1 by input order. The first rich text before the radio is the
 * headline, review stars move onto the image, and a question rich text (or
 * the radio's own label) becomes the divider. Rich text after the radio is
 * omitted so it cannot sit between the pills and the call link. Every other
 * input stays in document order.
 */
function mapButtonStackStep(step: FormRegularStep): ButtonStackStepMapping {
  const inputs = step.inputs ?? [];
  const radioIndex = inputs.findIndex((input) => input._type === "radioInput");

  if (radioIndex === -1) {
    return { kind: "fallback" };
  }

  const radioAtIndex = inputs[radioIndex];
  if (radioAtIndex?._type !== "radioInput") {
    return { kind: "fallback" };
  }

  let headline: RichTextStepItem | undefined;
  let reviewStars: ReviewStarsStepItem | undefined;
  const middleItems: StepItem[] = [];

  for (const input of inputs.slice(0, radioIndex)) {
    if (input._type === "richTextBlock" && !headline) {
      headline = input;
      continue;
    }

    if (input._type === "reviewStars" && !reviewStars) {
      reviewStars = input;
      continue;
    }

    middleItems.push(input);
  }

  const { middleItems: remainingMiddle, radioInput } =
    withDividerFromPrecedingRichText(radioAtIndex, middleItems);

  let subhead: RichTextStepItem | undefined;
  const flowBefore: StepItem[] = [];

  for (const input of remainingMiddle) {
    if (input._type === "richTextBlock" && !subhead) {
      subhead = input;
      continue;
    }

    flowBefore.push(withLimePhoneLink(input));
  }

  return {
    flowBefore,
    headline,
    kind: "buttonStack",
    radioInput,
    reviewStars,
    subhead,
    trailingItems: inputs
      .slice(radioIndex + 1)
      .filter((input) => input._type !== "richTextBlock")
      .map(withLimePhoneLink),
  };
}

/**
 * The first regular step can wear the button-stack shell only when it has a
 * radio to turn into the pill stack. Otherwise the step stays on
 * LeadFormStepItem.
 */
function findButtonStackStep(steps: FormStep[]): FormRegularStep | undefined {
  const firstRegularStep = steps.find(
    (step): step is FormRegularStep => step._type === "step",
  );

  if (!firstRegularStep) {
    return undefined;
  }

  const mapping = mapButtonStackStep(firstRegularStep);
  if (mapping.kind === "fallback") {
    return undefined;
  }

  return firstRegularStep;
}

export { findButtonStackStep, mapButtonStackStep };
